import { sleep } from './sleep';

const getRandomDelay = (minDelay, maxDelay) => Math.floor(Math.random() * (maxDelay - minDelay + 1)) + minDelay;

/**
 * Polls an async function until a condition is met or max retries are exhausted.
 *
 * @param {Function} fetchFn - async function to call on each attempt
 * @param {Function} conditionFn - function(result) => boolean, return true when the desired state is reached
 * @param {Object} [options]
 * @param {number} [options.maxRetries=8] - maximum number of retry attempts
 * @param {number} [options.minDelay=2000] - minimum delay between attempts in ms
 * @param {number} [options.maxDelay=5000] - maximum delay between attempts in ms
 * @returns {Promise<{ result: *, found: boolean }>}
 *   `found: true`  — condition was met, `result` is the successful fetch result
 *   `found: false` — max retries exhausted without condition being met, `result` is null
 *
 * @example
 * const { result, found } = await pollUntil(
 *     () => apiGetMeshScenes(),
 *     (res) => res?.data?.data?.list?.some((item) => item.uuid === targetUuid),
 *     { maxRetries: 8, minDelay: 2000, maxDelay: 5000 },
 * );
 */
export const pollUntil = async (fetchFn, conditionFn, { maxRetries = 8, minDelay = 2000, maxDelay = 5000 } = {}) => {
    let attempts = 0;

    while (attempts <= maxRetries) {
        await sleep(getRandomDelay(minDelay, maxDelay));

        try {
            const result = await fetchFn();

            if (conditionFn(result)) {
                return { result, found: true };
            }
        } catch {
            // continue polling on error
        }

        attempts++;
    }

    return { result: null, found: false };
};
